// The open to-do, in place of its row, the rest of the list stepping back: title, where it lives, its checklist, the
// chips and pickers, and Markdown notes shown formatted and edited as text (TodoEditor.swift). Title, notes and the
// checklist are saved when the card closes (Done, Esc, Return in the title, a click beside it); a picker's choice at once.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type Ref, type TextareaHTMLAttributes } from "react";
import { losesReminder, projectAreaChoice } from "../core/actions";
import { ageOn, congratulatedBy } from "../core/birthdays";
import type { BirthdayDay } from "../core/lists";
import type { Todo } from "../core/records";
import { currentLanguage, t } from "../i18n";
import { Checklist } from "./Checklist";
import { useWindow } from "./context";
import { afterFirstFrame } from "./frames";
import { formatDay, placeOf, splitEmoji } from "./format";
import { Icon } from "./Icon";
import { Markdown } from "./Markdown";
import { Menu, type MenuItem } from "./Menu";
import { PersonBadge } from "./Sharing";
import { completionSound } from "./sound";
import { tickedInCard } from "./tickedInCard";
import { applyChange, Properties, usePicker, type PropertyChange } from "./Properties";
import { askCountAnyway, cardComponent, HabitCardBody, habitTicked, tickAskingFirst, tickSound, useHabitMenu, useHabitOf } from "./Habits";
import { sortedHabits } from "../core/habits";

export function TodoCard({ todo }: { todo: Todo }) {
  const window = useWindow();
  const { model, state, openTodo, showToast } = window;
  const [title, setTitle] = useState(todo.title);
  const [notes, setNotes] = useState(todo.notes);
  const [checklist, setChecklist] = useState(todo.checklist);
  const [editingNotes, setEditingNotes] = useState(false);
  /** Ticked in the card: it closes once the tick and the strike have played here. */
  const [closesAfterTick, setClosesAfterTick] = useState(false);
  const [showsChecklist, setShowsChecklist] = useState(todo.checklist.length > 0);
  const picker = usePicker();
  const card = useRef<HTMLDivElement>(null);
  const titleField = useRef<HTMLTextAreaElement>(null);
  const latest = useRef({ title, notes, checklist, todo });
  latest.current = { title, notes, checklist, todo };
  // A habit's day (docs/63): the week, the streak, How Often and the reminder instead of the to-do's chips; Expand
  // beside the menu grows the card into the habit's page.
  const habit = useHabitOf(todo);

  useEffect(() => {
    titleField.current?.focus({ preventScroll: true });
    // Into view after the card's first frame, so measuring the page does not delay it (docs/58, idea 3).
    return afterFirstFrame(() => card.current?.scrollIntoView({ block: "nearest" }));
  }, []);

  /** The to-do with what is typed in the card. */
  const edited = useCallback((): Todo => {
    const { title: typed, notes: written, checklist: items, todo: current } = latest.current;
    return { ...current, title: typed.trim() === "" ? current.title : typed.trim(), notes: written, checklist: items.filter((item) => item.title.trim() !== "") };
  }, []);

  /**
   * The flame again, or Make a To-Do: what is typed reaches the habit (or its day) first. On a day off (the card of a
   * day not stored) the to-do is made on the habit's next planned day, a to-do of its own: this card closes and that
   * one's opens, as on the Mac. Otherwise this card goes on as the plain to-do.
   */
  const makeTodoAgain = () => {
    if (!habit) return;
    const dayOff = model.isUnstoredHabitDay(todo);
    model.update(edited());
    const made = model.turnIntoTodo(habit.habit.id);
    showToast(t("Made a to-do"), true);
    if (dayOff && made !== undefined) openTodo(made);
  };
  const habitItems = useHabitMenu(habit?.habit, () => openTodo(null), makeTodoAgain);

  const close = useCallback(() => {
    const next = edited();
    // A habit's day: on a day it isn't due the habit takes the changes; a new habit closed without a name goes.
    if (next.habitID !== undefined) model.finishHabitCard(next);
    else model.update(next);
    openTodo(null);
    requestAnimationFrame(() => {
      // The row it was stays chosen, unless something else began meanwhile (Quick Entry, Find).
      window.set((ui) => (ui.composing || ui.open || ui.finding ? {} : { selected: [next.id] }));
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected) {
        document.querySelector<HTMLElement>(`[data-row="${next.id}"]`)?.focus({ preventScroll: true });
      }
    });
  }, [edited, model, openTodo, window]);

  useEffect(() => {
    if (!closesAfterTick) return;
    const id = todo.id;
    const reduced = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const timer = setTimeout(close, reduced ? 150 : 500);
    // Once the to-do has stopped lingering (1.5 s), a later tick on its row pulses the ring again; this outlives
    // the card, which is gone by then.
    setTimeout(() => tickedInCard.delete(id), 2000);
    return () => clearTimeout(timer);
  }, [closesAfterTick, close, todo.id]);

  useEffect(() => {
    const listener = () => close();
    document.addEventListener("todolix:close-card", listener);
    return () => document.removeEventListener("todolix:close-card", listener);
  }, [close]);

  /** Into a project in this area: what is typed in the card is kept first; the project opens. */
  const convert = (areaID: string) => {
    model.update(edited());
    const project = model.convertToProject(todo.id, areaID);
    if (project) window.go({ kind: "project", id: project.id });
  };

  const change = (property: PropertyChange) => {
    if (property.convertIn !== undefined) return convert(property.convertIn);
    if (property.todaySection !== undefined) {
      model.update(edited());
      model.moveToSection([todo.id], property.todaySection ?? undefined, true);
      return;
    }
    if (property.skip) {
      model.update(edited());
      model.skip(todo.id);
      return;
    }
    const before = edited();
    const next = applyChange(before, property, state.today, state.zone);
    model.update(next, property.move ? "Move" : "Change");
    // Someday took the reminder away: said, with Undo (Bastian, 2026-10-20).
    if (losesReminder(before, next)) showToast(t("Reminder removed"), true);
  };

  const key = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement;
    // A picker's keys are its own (it is drawn outside the card): a letter there never opens another picker.
    if (!card.current?.contains(target)) return;
    const typing = target.matches("input, textarea, select");
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); close(); return; }
    if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
    const button = (slot: string) => card.current?.querySelector<HTMLElement>(`[data-slot="${slot}"]`);
    if (event.key === "w" || event.key === "W") { event.preventDefault(); const b = button("when"); if (b) picker.show("when", b); }
    if (event.key === "d" || event.key === "D") { event.preventDefault(); const b = button("deadline"); if (b) picker.show("deadline", b); }
  };

  const place = placeOf(todo, state.lists);
  // A to-do to congratulate names its birthday, found from its id (docs/59, 5): worked out again only when the to-do,
  // the birthdays or the day change, not on every key typed into the card. Hidden birthdays are out of sight.
  const birthdays = state.records.birthdays;
  const congratulates = useMemo(
    () => congratulatedBy(todo.id, birthdays.filter((birthday) => birthday.isHidden !== true), state.today),
    [todo.id, birthdays, state.today],
  );
  const placeTitle = place.kind === "area" ? `${splitEmoji(place.title).emoji ?? ""} ${splitEmoji(place.title).name}`.trim() : place.title;
  const items: MenuItem[] = habit ? [...habitItems] : [];
  if (!habit && todo.repeats && todo.completedAt === undefined) items.push({ label: t("Skip This Time"), icon: "skip", run: () => change({ skip: true }) });
  // A to-do in an area, directly or through its project, becomes a project there at once; one without an area
  // offers every area (Store.projectAreaChoice; the same on the Mac and the iPhone; Bastian, 2026-10-20).
  // Someone else's shared area takes no projects from this person (the rules), so it is neither the place nor offered.
  const choice = projectAreaChoice(todo, state.lists.sortedAreas, state.records.projects, (area) => model.shareOf({ kind: "area", id: area.id })?.youOwn ?? true);
  if (!habit && choice.kind !== "unavailable" && todo.completedAt === undefined) {
    items.push({
      label: t("Convert to Project"), icon: "folderPlus",
      run: () => {
        if (choice.kind === "area") return convert(choice.area.id);
        const b = card.current?.querySelector<HTMLElement>(".card-more");
        if (b) picker.show("convert", b);
      },
    });
  }
  // Into one of Today's sections (the iPhone's editor and the Mac's menu have it too): the way into a section that Today
  // hides while it is empty (Settings › Lists › Show Empty Sections).
  if (!habit && todo.completedAt === undefined && state.lists.isToday(todo) && state.lists.todaySections.length > 0) {
    items.push({ label: t("Today Section"), icon: "today", run: () => { const b = card.current?.querySelector<HTMLElement>(".card-more"); if (b) picker.show("todaySection", b); } });
  }
  if (!habit) items.push({ label: t("Move"), icon: "move", run: () => { const b = card.current?.querySelector<HTMLElement>(".card-where"); if (b) picker.show("move", b); } });
  if (!habit) items.push({
    label: t("Delete To-Do"), icon: "trash", destructive: true,
    run: () => { openTodo(null); model.delete([todo.id]); showToast(t("To-do deleted"), true); },
  });

  const done = todo.completedAt !== undefined;
  // A habit's day not stored (a day it isn't due, opened in Habits or an area): its box is quiet and asks first.
  const unplanned = habit !== null && model.isUnstoredHabitDay(todo);
  // In a shared list: who completed it, if it was someone else (docs/43; no more than that, for now).
  const completedBy = done ? model.completedByName(todo) : undefined;
  const current: Todo = { ...todo, checklist };

  return (
    <div ref={card} className={`row card${done ? " done" : ""}${habit ? " habit" : ""}`} data-card={todo.id} onKeyDown={key} role="group" aria-label={todo.title}>
      <div className="card-top">
        <button className={`check${unplanned ? " not-planned" : ""}`} aria-label={done ? t("Mark as Not Done") : t("Complete")} aria-pressed={done}
          onClick={() => {
            model.update(edited());
            if (done) { openTodo(null); model.toggleCompletion([todo.id]); return; }
            // A habit on a day it isn't due: asked first, then counted anyway; the card closes as after a tick.
            if (unplanned) {
              return askCountAnyway(window, edited(), () => { tickedInCard.add(todo.id); setClosesAfterTick(true); });
            }
            // Ticked: the moment plays in the card, which then closes onto a row already struck (Bastian, 2026-10-20).
            // The morning after a missed day a habit's day asks first (docs/70).
            tickAskingFirst(window, habit ? [todo] : [], () => {
              if (habit) tickSound([todo]); else completionSound(1);
              model.toggleCompletion([todo.id]);
              showToast(t("Completed"), true);
              if (habit) habitTicked(window, [todo]);
              tickedInCard.add(todo.id);
              setClosesAfterTick(true);
            });
          }}>
          <Icon name="check" />
        </button>
        <TitleField ref={titleField} value={title} placeholder={habit ? t("New Habit") : t("New To-Do")} aria-label={t("Title")}
          onText={setTitle}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); close(); }
            if (event.key === "Tab" && !event.shiftKey) tabToChips(event);
          }} />
        {habit && (
          <button className="card-expand" title={`${t("Expand")} (→)`} aria-label={t("Expand")}
            onClick={() => { model.update(edited()); window.set({ habitPage: habit.habit.id, open: null }); }}>
            <Icon name="expand" />
          </button>
        )}
        <Menu label={t("More")} items={items} icon="more" className="card-more" />
      </div>
      <div className="card-inner">
        {habit ? (
          <>
            <span className="card-where static">{placeTitle}</span>
            <HabitCardBody todo={current} habit={habit.habit} progress={habit.progress} badge={habit.badge} onMakeTodo={makeTodoAgain}
              onReminder={(next) => model.update({ ...edited(), reminder: next.reminder, ...(next.reminderZone ? { reminderZone: next.reminderZone } : {}) } as Todo)} />
          </>
        ) : (
          <>
            <button className="card-where" onClick={(event) => picker.show("move", event.currentTarget)} title={t("Move")}>
              {placeTitle}
            </button>
            {congratulates && <BirthdayLine link={congratulates} />}
            {(showsChecklist || checklist.length > 0) && <Checklist items={checklist} onChange={setChecklist} focusNew={checklist.length === 0} />}
            <Properties todo={current} picker={picker} onChange={change} tools={["when", "deadline", "reminder", "repeat", "tags", "checklist"]}
              convertAreas={choice.kind === "choose" ? choice.areas : undefined}
              onChecklist={() => setShowsChecklist(true)}
              before={done ? undefined : (
                <button className="tool flame-tool" data-slot="flame" title={t("Make It a Habit")} aria-label={t("Make It a Habit")}
                  onClick={() => {
                    if (!state.isPro && sortedHabits(state.records.habits).length >= 3) return window.showSheet({ kind: "pro", feature: "habits" });
                    model.update(edited());
                    if (model.makeHabit(todo.id)) showToast(t("Made a habit"), true);
                  }}>
                  <Icon name="flame" />
                </button>
              )} />
          </>
        )}
        {editingNotes || notes.trim() === "" ? (
          <NotesField value={notes} placeholder={t("Notes")} aria-label={t("Notes")}
            autoFocus={editingNotes} onText={setNotes} onBlur={() => setEditingNotes(false)} />
        ) : (
          <div className="notes shown" role="button" tabIndex={0} aria-label={t("Edit Notes")}
            onClick={(event) => { if (!(event.target as HTMLElement).closest("a")) setEditingNotes(true); }}
            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); setEditingNotes(true); } }}>
            <Markdown text={notes} />
          </div>
        )}
        {done && completedBy && (
          <div className="completed-by">
            <PersonBadge name={completedBy} size={18} />
            {t("Completed by {name}, {when}", { name: completedBy, when: relativeTime(todo.completedAt ?? new Date()) })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "Jonathan Selbie turns 49 · Mon, 19 Oct" under the title of a to-do to congratulate. The apps open the birthday on
 * a tap; the web has no birthdays to open yet (they are added and changed in the apps), so it only names it.
 */
function BirthdayLine({ link }: { link: BirthdayDay }) {
  const age = ageOn(link.birthday, link.day);
  const name = link.birthday.name;
  const label = age === undefined ? t("Birthday of {name}", { name }) : t("{name} turns {count}", { name, count: age });
  return (
    <div className="card-birthday">
      <Icon name="gift" />
      <span>{label} · {formatDay(link.day, { weekday: "short", day: "numeric", month: "short" })}</span>
    </div>
  );
}

/**
 * The title of the open to-do and of Quick Entry: it wraps and grows with its text, line by line, pushing what is below
 * it down, as on the iPhone and the Mac (a single-line field ran on out of sight; Bastian, 2026-10-28). Its height comes
 * from a hidden copy of the text in the same cell of a grid, so the browser sizes it in its own layout, without
 * measuring from script as the card opens (docs/58). A title is one line: a line break typed or pasted is a space.
 */
export function TitleField({ value, onText, ref, ...rest }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string;
  onText: (text: string) => void;
  ref?: Ref<HTMLTextAreaElement>;
}) {
  return (
    <div className="grows card-title-wrap" data-value={value}>
      <textarea ref={ref} className="card-title" rows={1} value={value} autoComplete="off" {...rest}
        onChange={(event) => onText(event.target.value.replace(/\r?\n/g, " "))} />
    </div>
  );
}

/**
 * The notes being written, in the open to-do and in Quick Entry: as tall as their text from the first frame, wrapped
 * lines too, the same way as the title (they had a row for each line break only, so a long paragraph showed one line
 * until something was typed).
 */
export function NotesField({ value, onText, ...rest }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string;
  onText: (text: string) => void;
}) {
  return (
    <div className="grows notes" data-value={value}>
      <textarea className="notes-field" rows={1} value={value} {...rest} onChange={(event) => onText(event.target.value)} />
    </div>
  );
}

/** Tab from the title goes to the chips row (docs/55, item 14); ⇧Tab from the first chip comes back through the rest. */
export function tabToChips(event: KeyboardEvent<HTMLElement>) {
  const first = event.currentTarget.closest(".card")?.querySelector<HTMLElement>(".chips button");
  if (!first) return;
  event.preventDefault();
  first.focus();
}

/** "2 hours ago", "yesterday": how long ago, in the page's language. */
function relativeTime(moment: Date, now = new Date()): string {
  const format = new Intl.RelativeTimeFormat(currentLanguage(), { numeric: "auto" });
  const minutes = Math.round((moment.getTime() - now.getTime()) / 60_000);
  if (Math.abs(minutes) < 60) return format.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(hours, "hour");
  return format.format(Math.round(hours / 24), "day");
}

// Habits shows a habit day's open card through this, without importing this module (Habits.tsx).
cardComponent.current = TodoCard;
